import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeactivationType } from '@vendasta/accounts/legacy';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ANY_ADDON, ANY_EDITION, AppsService } from '../../../../data-services/apps.service';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { ADDON_NOT_FOUND, ALL_PRODUCTS, ANY, PRODUCT_NOT_FOUND } from './constants';

const INVALID_DEACTIVATION_TYPE = 'Invalid deactivation type configured for action';

/**
 * Deactivates one or more products.
 */
@Injectable({ providedIn: 'root' })
export class DeactivateProductService extends AutomationDisplayService {
  constructor(
    protected translateService: TranslateService,
    private appsService: AppsService,
  ) {
    super();
  }

  private activityError(reason: string): ActivityTableEntry {
    switch (reason) {
      case '': {
        return this.tableEntry(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.ACTIVITY_LOADING_FAILED'),
          this.emptyBody(),
        );
      }
      case PRODUCT_NOT_FOUND: {
        return this.tableEntry(
          this.translateService.stream(
            'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.DEACTIVATE_PRODUCT_FAILURE',
          ),
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.PRODUCT_NOT_FOUND'),
        );
      }
      case ADDON_NOT_FOUND: {
        return this.tableEntry(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.DEACTIVATE_ADDON_FAILURE'),
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.ADDON_NOT_FOUND'),
        );
      }
      default: {
        return this.tableEntry(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.ACTIVITY_LOADING_FAILED'),
          this.emptyBody(),
        );
      }
    }
  }

  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const stepData = activity?.workflowStep?.data ? JSON.parse(activity?.workflowStep?.data) : {};

    const deactivateType = stepData.deactivation_type;
    const appId = activityData?.app_id || '';
    const editionId = activityData?.edition_id;
    const addonId = activityData?.addon_id;

    if (appId === '' || appId === PRODUCT_NOT_FOUND || appId === ADDON_NOT_FOUND) {
      return this.activityError(appId);
    }

    const productName$ = this.getProductName(appId, editionId, addonId);
    let deactivationTitleKey: string;
    switch (deactivateType) {
      case DeactivationType.DEACTIVATION_TYPE_CANCEL:
        deactivationTitleKey = 'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.CANCEL';
        break;
      case DeactivationType.DEACTIVATION_TYPE_IMMEDIATE:
        deactivationTitleKey = 'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.DEACTIVATE';
        break;
      default:
        return this.activityError('');
    }
    const title$ = productName$.pipe(
      switchMap((productName) => this.translateService.stream(deactivationTitleKey, { productName })),
      catchError((err) => {
        console.error(err);
        return this.translateService.stream(
          'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTIVITY.ACTIVITY_LOADING_FAILED',
        );
      }),
    );
    return this.tableEntry(title$, this.emptyBody());
  }

  getProductName(appId: string, editionId: string, addonId: string): Observable<string> {
    // Otherwise, figure out the deactivation type and return the appropriate text.
    let productName$: Observable<string>;
    if (appId === ALL_PRODUCTS) {
      productName$ = this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ALL_PRODUCTS');
    } else {
      productName$ = this.appsService.getProductName({ appId, editionId }, true);
    }
    if (
      addonId !== '' &&
      addonId !== ANY &&
      addonId !== ANY_ADDON &&
      addonId !== ANY_EDITION &&
      appId !== ALL_PRODUCTS
    ) {
      // Verifies we aren't in any case where add-ons aren't set to any of the predefined values meaning it must be an add-on ID
      productName$ = combineLatest([productName$, this.appsService.getAddons(appId)]).pipe(
        map(
          ([productName, addons]) =>
            productName.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) + ` | ${addons.find((a) => a.addonId === addonId).title}`,
        ),
      );
    } else if (addonId === ANY_ADDON && editionId === ANY_EDITION && appId !== ALL_PRODUCTS) {
      // Verfifies if it's not a specific ID then it must be set for any add-on
      productName$ = productName$.pipe(
        map(
          (n) =>
            n.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) +
            ` (${this.translateService.instant(
              'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ALL_ADDONS',
            )})`.toLowerCase(),
        ),
      );
    } else if (addonId === ANY && editionId === ANY_EDITION && appId !== ALL_PRODUCTS) {
      // Verfifies if it's not a specific ID or any addonId then it must be set for all editions and add-ons
      productName$ = productName$.pipe(
        map(
          (n) =>
            n.replace(
              ` (${this.translateService
                .instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION')
                .toLowerCase()})`,
              '',
            ) +
            ` (${this.translateService
              .instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.EDITION_AND_ADDON')
              .toLowerCase()})`,
        ),
      );
    }
    return productName$;
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};

    const deactivateType = data.deactivation_type;
    const appId = data.app_id || '';
    const editionId = data.edition_id;
    const addonId = data.addon_id;

    // If there's no app ID, something's gone wrong.
    if (appId === '') {
      return this.nodeDisplayData(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.DEACTIVATE_PRODUCT'),
        this.nonDismissibleValidationErrorsDetected(
          step,
          this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.CONFIG_ERROR'),
        ),
      );
    }

    const productName$ = this.getProductName(appId, editionId, addonId);
    const deactivateType$ = of(deactivateType);
    return combineLatest([deactivateType$, productName$])
      .pipe(
        switchMap(([deactivationType, productName]) => {
          switch (deactivationType) {
            case DeactivationType.DEACTIVATION_TYPE_CANCEL:
              return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTION.CANCEL', {
                productName,
              });
            case DeactivationType.DEACTIVATION_TYPE_IMMEDIATE:
              return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTION.DEACTIVATE', {
                productName,
              });
            default:
              return throwError(INVALID_DEACTIVATION_TYPE);
          }
        }),
      )
      .pipe(
        switchMap((text) => {
          return this.nodeDisplayData(of(text), this.noValidationErrorsDetected());
        }),
        catchError((err) => {
          console.error(err);
          return this.nodeDisplayData(
            this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ACTION.ACTION_LOADING_FAILED'),
            this.nonDismissibleValidationErrorsDetected(
              step,
              this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.CONFIG_ERROR'),
            ),
          );
        }),
      );
  }

  getTemplateNodeData(
    _namespace: string,
    _step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.DEACTIVATE_PRODUCT'),
      this.noValidationErrorsDetected(),
    );
  }
}
